import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getUserAccountSubscriptionHistory } from "../../../common/services/marketdata/userAccountSubscriptionService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const UserAccountSubscriptionHistory = ({ userAccountSubscriptionId }) => {
  const { t } = useTranslation(["auth"]);
  const [state, setState] = useState({
    userAccountSubscriptionHistory: [],
    isLoading: true,
  });

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 100,
    }),
    []
  );

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: t("RecordID") },
      { field: "login", headerName: t("UserAccountName") },
      {
        field: "organizationSubscriptionName",
        headerName: t("OrganizationSubscription"),
      },
      {
        field: "contributor",
        headerName: t("Contributor"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.contributorString,
        cellRenderer: (params) => {
          const isActive = params.data?.contributor;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      { field: "updatedBy", headerName: t("UpdatedBy") },
      { field: "updatedDate", headerName: t("UpdateDate") },
      { field: "writeDate", headerName: t("WriteDate") },
      { field: "eventId", headerName: t("EventID") },
    ],
    [t]
  );

  // Memoize grid options for optimal rendering
  const gridOptions = useMemo(
    () => ({
      pagination: true,
      paginationPageSize: 20,
      domLayout: "autoHeight",
    }),
    []
  );

  // Map subscription data to view model format
  const mapToViewModel = useCallback((userAccountSubscriptions) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return userAccountSubscriptions.map((userAccountSubscription) => ({
      id: userAccountSubscription.id,
      login: userAccountSubscription.login,
      organizationSubscriptionName:
        userAccountSubscription.organizationSubscriptionName,
      contributor: userAccountSubscription.contributor,
      active: userAccountSubscription.active,
      updatedByUserAccountId: userAccountSubscription.updatedByUserAccountId,
      updatedBy:
        userAccountSubscription.updatedByUserAccountFirstName +
        " " +
        userAccountSubscription.updatedByUserAccountLastName,
      updatedDate: new Date(
        userAccountSubscription.updatedDate
      ).toLocaleDateString("en-US", dateFormatOptions),
      writeDate: new Date(userAccountSubscription.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: userAccountSubscription.eventId,
    }));
  }, []);

  // Load subscription history data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const historyData = await getUserAccountSubscriptionHistory(
          userAccountSubscriptionId
        );
        const mappedData = mapToViewModel(historyData);

        setState({
          userAccountSubscriptionHistory: _.orderBy(mappedData, "id", "desc"),
          isLoading: false,
        });
      } catch (error) {
        console.error(
          "Error fetching user account subscription history:",
          error
        );
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [userAccountSubscriptionId, mapToViewModel]);

  const { userAccountSubscriptionHistory, isLoading } = state;

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container auth-container">
      {t("UserAccountSubscriptionID")} {userAccountSubscriptionId}
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={userAccountSubscriptionHistory}
        gridOptions={gridOptions}
        getRowId={(params) => params.data.id.toString()}
        animateRows={true}
      />
    </div>
  );
};

export default UserAccountSubscriptionHistory;

